import {faTimes} from '@fortawesome/pro-light-svg-icons'
import {Dialog as BaseDialog, DialogPanel, DialogTitle, Popover, PopoverOverlay, Portal} from '@headlessui/react'
import {AnimatePresence} from 'framer-motion'
import {useEffect, useRef} from 'react'

import {noop, twMerge} from '@sdox/utils'

import {AnimateOpacity} from '../AnimateOpacity'
import {FaIcon} from '../FaIcon'

import type {ComponentProps} from 'react'

export interface DialogRootProps extends ComponentProps<'div'> {
  title?: string
  open: boolean
  size?: 'small' | 'medium' | 'large' | 'screen'
  persistent?: boolean
  onClose?(): void
}

const sizes = {
  small: 'max-w-xl w-full sm:w-10/12',
  medium: 'max-w-5xl w-full sm:w-10/12',
  large: 'max-w-7xl w-full sm:w-10/12',
  screen: 'w-full max-w-screen-lg',
}

export const DialogRoot = ({
  title,
  children,
  open,
  size = 'medium',
  persistent = false,
  onClose = noop,
  className,
}: DialogRootProps) => {
  const contentRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: 0,
        left: 0,
      })
    }
  }, [children])

  return (
    <Portal>
      <AnimatePresence mode={'wait'}>
        {open && (
          <BaseDialog
            static
            open={open}
            className={twMerge('fixed inset-0 z-[100] overflow-hidden', className)}
            onClose={!persistent ? onClose : noop}
          >
            <AnimateOpacity isVisible={open}>
              <div
                className={twMerge('min-h-screen w-screen bg-black/30')}
                style={{height: '-webkit-fill-available'}}
              >
                <div className={twMerge('flex h-screen w-screen items-center justify-center p-2')}>
                  <Popover>
                    <PopoverOverlay className={'fixed inset-0 bg-black/10'} />
                  </Popover>
                  <DialogPanel
                    as={'div'}
                    //eslint-disable-next-line tailwindcss/no-custom-classname
                    className={twMerge(
                      'fixed mx-auto flex flex-col overflow-hidden bg-surface-primary text-left align-middle shadow-xl transition-all',
                      sizes[size],
                    )}
                    style={{maxHeight: size === 'screen' ? '95%' : '90%'}}
                  >
                    {title && (
                      <>
                        <div
                          className={twMerge(
                            'flex flex-1 items-center justify-between gap-4 p-5 py-4 sm:px-12 sm:py-6',
                            className,
                          )}
                        >
                          {title && (
                            <DialogTitle
                              as={'h3'}
                              className={'font-medium leading-6 text-gray-900 sm:text-lg'}
                            >
                              {title}
                            </DialogTitle>
                          )}
                          {!persistent && (
                            <button
                              aria-label={'Dialog schließen'}
                              onClick={onClose}
                              className={'size-5 sm:size-8'}
                            >
                              <FaIcon
                                className={twMerge('size-full text-gray-500')}
                                icon={faTimes}
                              />
                            </button>
                          )}
                        </div>
                        <hr />
                      </>
                    )}
                    <div
                      className={'h-full overflow-auto'}
                      ref={contentRef}
                    >
                      {children}
                    </div>
                  </DialogPanel>
                </div>
              </div>
            </AnimateOpacity>
          </BaseDialog>
        )}
      </AnimatePresence>
    </Portal>
  )
}
